/**
 * ArReceiptVoucher — Printable transaction voucher for Customer Receipts.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { ArReceiptDto, ArReceiptLineDto, ArReceiptAllocationDto } from '@/types/arReceipt.types';
import { PrintableTable } from '@/components/master/report/PrintableTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  Cash:          'Cash',
  Cheque:        'Cheque',
  BankTransfer:  'Bank Transfer',
  CreditCard:    'Credit Card',
  DebitCard:     'Debit Card',
  OnlineBanking: 'Online Banking',
  Other:         'Other',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '120px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:    { bg: '#f3f4f6', text: '#374151' },
    Submitted:{ bg: '#dbeafe', text: '#1e40af' },
    Approved: { bg: '#d1fae5', text: '#065f46' },
    Rejected: { bg: '#fee2e2', text: '#991b1b' },
    Posted:   { bg: '#a7f3d0', text: '#064e3b' },
    Cancelled:{ bg: '#f3f4f6', text: '#6b7280' },
    Reversed: { bg: '#ede9fe', text: '#5b21b6' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ArReceiptVoucherProps {
  receipt:     ArReceiptDto;
  companyName: string;
  printedBy?:  string;
}

export const ArReceiptVoucher = ({
  receipt,
  companyName,
  printedBy,
}: ArReceiptVoucherProps): ReactNode => {

  // What this receipt settles (ML-08), so the customer's remittance can be reconciled against
  // the invoices it actually paid rather than guessed at from a total.
  const allocationColumns = [
    { key: 'lineNumber',      label: '#',            width: '28px', align: 'center' as const,
      render: (a: ArReceiptAllocationDto) => a.lineNumber },
    { key: 'documentNumber',  label: 'Invoice No.',  width: '120px',
      render: (a: ArReceiptAllocationDto) => a.documentNumber },
    { key: 'documentDate',    label: 'Date',         width: '80px',
      render: (a: ArReceiptAllocationDto) => a.documentDate ? fmtDate(a.documentDate) : '' },
    { key: 'dueDate',         label: 'Due',          width: '80px',
      render: (a: ArReceiptAllocationDto) => a.dueDate ? fmtDate(a.dueDate) : '' },
    { key: 'remarks',         label: 'Remarks',
      render: (a: ArReceiptAllocationDto) => a.remarks || '' },
    { key: 'allocatedAmount', label: 'Settled',      width: '90px', align: 'right' as const,
      render: (a: ArReceiptAllocationDto) => <strong>{fmtNum(a.allocatedAmount)}</strong> },
  ];

  const lineColumns = [
    { key: 'lineNumber', label: '#',            width: '28px',  align: 'center' as const,
      render: (l: ArReceiptLineDto) => l.lineNumber },
    { key: 'paymentType', label: 'Payment Type', width: '110px',
      render: (l: ArReceiptLineDto) => PAYMENT_TYPE_LABELS[l.paymentType] ?? l.paymentType },
    { key: 'chequeNumber', label: 'Cheque No.',  width: '90px',
      render: (l: ArReceiptLineDto) => l.chequeNumber || '' },
    { key: 'chequeDate', label: 'Cheque Date',   width: '80px',
      render: (l: ArReceiptLineDto) => l.chequeDate ? fmtDate(l.chequeDate) : '' },
    { key: 'cardReference', label: 'Ref / Card', width: '100px',
      render: (l: ArReceiptLineDto) => l.cardReference || '' },
    { key: 'currencyCode', label: 'CCY',         width: '40px',
      render: (l: ArReceiptLineDto) => l.currencyCode },
    { key: 'amount', label: 'Amount',            width: '90px',  align: 'right' as const,
      render: (l: ArReceiptLineDto) => <strong>{fmtNum(l.amount)}</strong> },
    { key: 'remarks', label: 'Remarks',
      render: (l: ArReceiptLineDto) => l.remarks || '' },
  ];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
              {receipt.isReversal ? 'Reversal Receipt' : 'Receipt Voucher'}
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {receipt.receiptNumber}
            </div>
            <div style={{ marginTop: '4px' }}>
              <StatusBadge status={receipt.workflowStatus} />
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Customer + Document details ─────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Received From
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{receipt.customerName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{receipt.customerCode}</div>
          {receipt.reference && (
            <div style={{ fontSize: '10px', color: '#374151', marginTop: '4px' }}>
              <span style={{ color: '#6b7280' }}>Reference:</span> {receipt.reference}
            </div>
          )}
        </div>
        <div>
          <LabelValue label="Receipt Date"    value={fmtDate(receipt.receiptDate)} />
          <LabelValue label="Posting Date"    value={fmtDate(receipt.postingDate)} />
          <LabelValue label="Payment Method"  value={PAYMENT_TYPE_LABELS[receipt.paymentMethod] ?? receipt.paymentMethod} />
          <LabelValue label="Currency"        value={`${receipt.currencyCode} (Rate: ${receipt.exchangeRate.toFixed(4)})`} />
          <LabelValue label="Period"          value={`${receipt.financialPeriodCode} / ${receipt.financialYearCode}`} />
        </div>
      </div>

      {/* Narration */}
      {receipt.narration && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Narration: </span>
          {receipt.narration}
        </div>
      )}

      {/* ── Lines table ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Payment Details
      </div>

      <PrintableTable
        columns={lineColumns}
        rows={receipt.lines ?? []}
        rowKey={(l) => l.id}
      />

      {/* ── Totals ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '4px',
        marginBottom: '20px',
      }}>
        <div style={{ width: '200px' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '5px 8px',
            background: '#1e3a5f',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
          }}>
            <span>Total ({receipt.currencyCode}):</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtNum(receipt.totalAmount)}</span>
          </div>
          {receipt.exchangeRate !== 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
              <span>Base Amount:</span>
              <span style={{ fontFamily: 'monospace' }}>{fmtNum(receipt.baseAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Invoices Settled (ML-08) ────────────────────────────────────────── */}
      {(receipt.allocations?.length ?? 0) > 0 && (
        <>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Invoices Settled
          </div>

          <PrintableTable
            columns={allocationColumns}
            rows={receipt.allocations ?? []}
            rowKey={(a) => a.id}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px', marginBottom: '20px' }}>
            <div style={{ width: '220px', display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', borderTop: '1px solid #1e3a5f' }}>
              <span>Total Settled ({receipt.currencyCode}):</span>
              <span style={{ fontFamily: 'monospace' }}>
                {fmtNum((receipt.allocations ?? []).reduce((t, a) => t + a.allocatedAmount, 0))}
              </span>
            </div>
          </div>
        </>
      )}

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {[
          { label: 'Received By' },
          { label: 'Verified By' },
          { label: 'Authorised By' },
        ].map(({ label }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
